import React from "react";
import { Phone, MapPin, Instagram, Send, MessageCircle, ArrowUpRight, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { BFLogo } from "./BFLogo";
import { WhatsAppIcon } from "./icons/WhatsAppIcon";
import { phones } from "../i18n/content";
import footerMark from "../assets/brand/logo_3d_footer_mark.webp";
import footerRegion from "../assets/sections/footer-region.webp";

const NAV_PATHS = {
  home: "/",
  products: "/products",
  news: "/news",
  about: "/about",
  reporting: "/reporting",
};

const TWO_GIS_URL = "https://2gis.kz/uralsk/firm/70000001029568964/tab/info";
const INSTAGRAM_URL = "https://www.instagram.com/batys_finance/";
const TELEGRAM_BOT_URL = "https://t.me/BatysFinanceBot";
// wa.me needs the number in international format, digits only.
const WHATSAPP_URL = "https://wa.me/77770754343";

const SOCIAL_ICON_CLASS =
  "flex h-10 w-10 items-center justify-center rounded-full border border-[#f5f7fa]/15 text-[#f5f7fa]/80 transition-colors duration-300 hover:border-transparent hover:bg-[#e8a93d] hover:text-[#002355]";

export function Footer() {
  const { c, openApply } = useApp();
  const navigate = useNavigate();
  const f = c.footer;

  return (
    <footer data-testid="site-footer" className="grain relative overflow-hidden bg-[#001a40] text-[#f5f7fa]">
      <img
        src={footerRegion}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center opacity-45"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(0,26,64,0.94)_0%,rgba(0,26,64,0.76)_48%,rgba(0,26,64,0.52)_100%)]"
      />
      {/* CTA band */}
      <div className="relative border-b border-[#f5f7fa]/10">
        <div className="mx-auto flex max-w-[1240px] flex-col items-start justify-between gap-6 px-5 py-16 md:flex-row md:items-center md:px-8">
          <h2 className="max-w-xl font-display text-3xl font-bold tracking-tight sm:text-4xl">
            {c.hero.titleLines.join(" ")}
          </h2>
          <button
            data-testid="footer-apply-btn"
            onClick={openApply}
            className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-[#e8a93d] px-7 py-4 text-base font-bold text-[#002355] transition-transform duration-300 hover:scale-105"
          >
            {c.cta.apply}
            <ArrowUpRight size={20} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </div>

      <div className="relative mx-auto grid max-w-[1240px] gap-12 px-5 py-16 md:grid-cols-[1.4fr_1fr_1fr] md:px-8">
        <div>
          <BFLogo brand={c.brand} tone="cream" size={176} src={footerMark} />
          <p className="mt-6 max-w-xs text-sm leading-relaxed text-[#f5f7fa]/65">{f.about}</p>
          <div className="mt-6 flex gap-3">
            <a
              href={INSTAGRAM_URL}
              data-testid="social-0"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              title="Instagram"
              className={SOCIAL_ICON_CLASS}
            >
              <Instagram size={18} />
            </a>

            <a
              href={TELEGRAM_BOT_URL}
              data-testid="social-1"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Telegram"
              title="Telegram"
              className={SOCIAL_ICON_CLASS}
            >
              <Send size={18} />
            </a>

            <a
              href={WHATSAPP_URL}
              data-testid="social-whatsapp"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp"
              title="WhatsApp"
              className={SOCIAL_ICON_CLASS}
            >
              <WhatsAppIcon size={18} />
            </a>

            <button
              type="button"
              data-testid="social-2"
              onClick={openApply}
              aria-label={c.cta.apply}
              title={c.cta.apply}
              className={SOCIAL_ICON_CLASS}
            >
              <MessageCircle size={18} />
            </button>
          </div>
        </div>

        <div>
          <h3 className="text-xs font-bold uppercase tracking-[0.16em] text-[#e8a93d]">{f.contactsTitle}</h3>
          <div className="mt-5 space-y-3">
            <div className="flex items-start gap-3">
              <Phone size={16} className="mt-1 shrink-0 text-[#e8a93d]" />
              <div className="space-y-1">
                {phones.map((p) => (
                  <a key={p} href={`tel:${p.replace(/[^\d+]/g, "")}`} className="block text-sm text-[#f5f7fa]/85 transition-colors hover:text-[#e8a93d]">
                    {p}
                  </a>
                ))}
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin size={16} className="mt-1 shrink-0 text-[#e8a93d]" />
              <div className="space-y-3">
                <a
                  href={TWO_GIS_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="block text-sm text-[#f5f7fa]/85 transition-colors hover:text-[#e8a93d]"
                >
                  {f.address}
                </a>
                <a
                  href={TWO_GIS_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#e8a93d]/60 px-3 py-1.5 text-xs font-semibold text-[#e8a93d] transition-colors hover:border-[#e8a93d] hover:bg-[#e8a93d] hover:text-[#002355]"
                >
                  Открыть в 2ГИС
                  <ExternalLink size={13} />
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 sm:grid-cols-2">
          <div>
            <h3 className="text-xs font-bold uppercase tracking-[0.16em] text-[#e8a93d]">{f.menuTitle}</h3>
            <ul className="mt-5 space-y-2.5">
              {c.nav.map((item) => (
                <li key={item.id}>
                  <button
                    data-testid={`footer-nav-${item.id}`}
                    onClick={() => navigate(NAV_PATHS[item.id] || "/")}
                    className="text-sm text-[#f5f7fa]/75 transition-colors duration-300 hover:text-[#e8a93d]"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
              <li>
                <a
                  data-testid="footer-calculator-link"
                  href="/products#calculator"
                  className="text-sm text-[#f5f7fa]/75 transition-colors duration-300 hover:text-[#e8a93d]"
                >
                  {c.cta.calculator}
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-xs font-bold uppercase tracking-[0.16em] text-[#e8a93d]">{f.legalTitle}</h3>
            <ul className="mt-5 space-y-2.5">
              {f.legal.map((l) => (
                <li key={l} className="text-sm text-[#f5f7fa]/65">{l}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="relative mx-auto flex max-w-[1240px] flex-col gap-2 px-5 py-8 text-xs text-[#f5f7fa]/45 md:flex-row md:items-center md:justify-between md:px-8">
        <p>© {new Date().getFullYear()} {f.legal[0]}. {f.rights}</p>
        <p>{f.disclaimer}</p>
      </div>
    </footer>
  );
}
